import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { iif, Observable, of } from 'rxjs';
import { catchError, debounceTime, filter, map, mergeMap, startWith } from 'rxjs/operators';
import { ActionCreators, requestActions } from './apiHelpers';
import Note, { NoteType } from '@repo/utils/models/Note';
import Contact from '@repo/utils/models/Contact';
import AppState from '../reducers/app-state';
import { ID, ReduxAction, AppView } from '@repo/utils/types';
import { apiObservable } from '@repo/utils/helpers/api';
import { selectNewNoteType } from '../reducers/data/notes';

/* Epic actions */
const noteActions = requestActions('NOTE', ['fetchAll', 'fetch', 'create', 'update', 'delete']);

// These actions are specific to auto-saving
const currentlyEditingNoteActions = requestActions('CURRENTLY_EDITING_NOTE', ['create', 'update']);
const pendingUpdateAction = (payload: ID) => ({ type: 'UPDATE_NOTE_DELAYED', payload });
const pendingDeleteAction = (payload: ID) => ({ type: 'DELETE_NOTE_DELAYED', payload });

/* Component actions */
export const fetchAllNotes = noteActions['fetchAll'];
export const fetchNote = (contactID: ID, noteID: ID) =>
  noteActions['fetch']({ contactID, id: noteID });
export const createNote = noteActions['create'];
export const updateNote = noteActions['update'];
export const deleteNote = noteActions['delete'];
export const setCurrentlyEditingNote = (payload: Note | null) => ({
  type: 'SET_CURRENTLY_EDITING_NOTE',
  payload,
});
export const updateCurrentlyEditingNote = currentlyEditingNoteActions['update'];

export const updateNoteType = (payload: { noteID: ID; noteType: NoteType }) => ({
  type: 'UPDATE_NOTE_TYPE',
  payload,
});

export const updateNewNoteType = (payload: NoteType) => ({
  type: 'UPDATE_NEW_NOTE_TYPE',
  payload,
});

export const cacheNewNote = (note: Note) => ({
  type: 'CREATE_NOTE_SUCCEEDED',
  payload: { response: note },
});

// In the future, we may want to remove Search query management from the ContactView component and use an epic
// and separate contacts slice of the redux store. For now though we'll manage merging of notes using this single
// action creator to be dispatched when a contact is successfully loaded
export const fetchContactSuccess = (contact: Contact) => ({
  type: 'FETCH_CONTACT_SUCCEEDED',
  payload: contact,
});

/* Selectors */
export const selectNote = (noteID: ID) => (state: AppState) =>
  state.data.notes.collection.items.get(noteID);

export const currentlyEditingNoteSelector = (state: AppState) => state.data.notes.currentlyEditing;

// This is for cases where we want to prefer the user's current state of the note over the persisted state.
// We can use this to do immediate updates on a note header or other related UI while the note is being edited.
export const selectRelevantNoteState = (noteID: ID) => (state: AppState) => {
  const currentlySelectedNote = currentlyEditingNoteSelector(state);
  return currentlySelectedNote?.id == noteID ? currentlySelectedNote : selectNote(noteID)(state);
};

// In the future, we may want to store contacts in the redux store. but contact notes should remain divorced from whatever we store there.
export const contactNotesSelector = (contactId: ID) => (state: AppState) =>
  state.data.notes.collection.items
    .filter((note: Note) => note.contact.id == contactId)
    .valueSeq()
    .toArray();

/* Helpers used in Epics */
const urlFromIds = (contactId: ID, noteId?: ID): string => {
  let url = `/network/contacts/${contactId}/notes`;
  return noteId ? `${url}/${noteId}` : url;
};

const patchObservable = (
  contactId: ID,
  noteID: ID,
  note: Note,
  view: AppView,
  actionCreators: ActionCreators<'update'>,
) =>
  apiObservable('PATCH', urlFromIds(contactId, noteID), note).pipe(
    map((response) => actionCreators['updateSucceeded']({ ...response, noteID, view })),
    startWith(actionCreators['updateRequested']({ id: noteID })),
    catchError((err) => of(actionCreators['updateFailed'](err))),
  );

const deleteObservable = (contactId: ID, noteId: ID) =>
  apiObservable('DELETE', urlFromIds(contactId, noteId)).pipe(
    map(noteActions['deleteSucceeded']),
    catchError((err) => of(noteActions['deleteFailed'](err))),
  );

/* Epics */
const fetchAllNotesEpic = (action$: Observable<ReduxAction>, state$: StateObservable<AppState>) =>
  action$.pipe(
    ofType('FETCH_ALL_NOTES'),
    mergeMap(() =>
      apiObservable('GET', '/network/notes', {
        query: {
          limit: state$.value.data.notes.indexViewCollection.limit,
          offset: state$.value.data.notes.indexViewCollection.offset,
        },
      }).pipe(
        map(noteActions['fetchAllSucceeded']),
        startWith(noteActions['fetchAllRequested']()),
        catchError((err) => of(noteActions['fetchAllFailed'](err))),
      ),
    ),
  );

const fetchNoteEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('FETCH_NOTE'),
    mergeMap(({ payload }) =>
      apiObservable('GET', urlFromIds(payload.contactID, payload.id)).pipe(
        map(noteActions['fetchSucceeded']),
        startWith(noteActions['fetchRequested']()),
        catchError((err) => of(noteActions['fetchFailed'](err))),
      ),
    ),
  );

export const createNoteEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('CREATE_NOTE'),
    mergeMap(({ payload }) =>
      apiObservable('POST', urlFromIds(payload.contact.id), payload).pipe(
        map((response) =>
          noteActions['createSucceeded']({
            ...response,
            originalRequestID: payload.id,
            view: state$.value.ui.location.view,
          }),
        ),
        startWith(noteActions['createRequested']()),
        catchError((err) =>
          of(
            noteActions['createFailed']({
              ...err,
              originalRequestID: payload.id,
              originalNote: payload,
            }),
          ),
        ),
      ),
    ),
  );

export const updateNoteEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('UPDATE_NOTE'),
    mergeMap(({ payload }) =>
      apiObservable('PATCH', urlFromIds(payload.contact.id, payload.id), payload).pipe(
        map((response) => noteActions['updateSucceeded']({ ...response, noteID: payload.id })),
        startWith(
          noteActions['updateRequested']({
            ...payload,
            originalNoteState: state$.value.data.notes.collection.items.get(String(payload.id)),
          }),
        ),
        catchError((err) => of(noteActions['updateFailed']({ ...err, noteID: payload.id }))),
      ),
    ),
  );

// If note isn't persisted but is currently saving, we will handle the delete once we have the note's ID returned
// from the server. See pendingRequestsEpic below.
export const deleteNoteEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('DELETE_NOTE'),
    mergeMap(({ payload }) =>
      iif(
        () => !payload.isPersisted && payload.isFetching,
        of(payload.id).pipe(map(pendingDeleteAction)),
        deleteObservable(payload.contact.id, payload.id),
      ),
    ),
  );

const autoSaveModalNoteEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('UPDATE_CONTACT_AND_NOTE_MODAL'),
    debounceTime(500),
    filter(({ payload }) => {
      const noteInStore = selectNote(payload.note?.id)(state$.value);
      return (
        payload.note?.isPersisted &&
        (noteInStore?.content !== payload.note.content ||
          noteInStore?.type.color !== payload.note.type.color ||
          noteInStore?.type.icon !== payload.note.type.icon ||
          noteInStore?.type.label !== payload.note.type.label)
      );
    }),
    mergeMap(({ payload }) =>
      patchObservable(
        payload.note.contact.id,
        payload.note.id,
        payload.note,
        state$.value.ui.location.view,
        noteActions,
      ),
    ),
  );

const autoSaveNoteTypeEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('UPDATE_NOTE_TYPE'),
    debounceTime(400),
    map(({ payload }) => {
      const note = selectNote(payload.noteID)(state$.value);
      return updateNote(note);
    }),
  );

// if this gets called and the currentlyEditingNote is persisted, autosave that note with this change applied
const autoSaveNewNoteTypeEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('UPDATE_NEW_NOTE_TYPE'),
    debounceTime(400),
    map(({ payload }) => {
      const currentlyEditingNote = currentlyEditingNoteSelector(state$.value);

      if (currentlyEditingNote?.isPersisted) {
        return updateNote({
          ...currentlyEditingNote,
          type: payload,
        });
      }

      return { type: 'NOOP' };
    }),
  );

export const autoSaveNoteEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('UPDATE_CURRENTLY_EDITING_NOTE'),
    debounceTime(750),
    mergeMap(({ payload }) => {
      const newNoteType = selectNewNoteType(state$.value);

      return iif(
        () => payload.isPersisted,
        patchObservable(
          payload.contact.id,
          payload.id,
          payload,
          state$.value.ui.location.view,
          currentlyEditingNoteActions,
        ),
        // If note is currently saving, dispatch a pending update. This req will be handled once the note is persisted.
        iif(
          () => !payload.isFetching,
          apiObservable('POST', urlFromIds(payload.contact.id), {
            ...payload,
            type: newNoteType,
          }).pipe(
            map((response) =>
              currentlyEditingNoteActions['createSucceeded']({
                ...response,
                originalRequestID: payload.id,
                view: state$.value.ui.location.view,
              }),
            ),
            startWith(currentlyEditingNoteActions['createRequested']()),
            catchError((err) => of(currentlyEditingNoteActions['createFailed'](err))),
          ),
          of(payload.id).pipe(map(pendingUpdateAction)),
        ),
      );
    }),
  );

// This handles any API requests that have been queued up for a Note while an initial POST request
// was in flight. We can update the note state locally in response to user actions while a POST is
// in flight, but we can't persist those changes until we have a note ID returned in the POST response.
export const pendingRequestsEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) => {
  return action$.pipe(
    ofType('CREATE_CURRENTLY_EDITING_NOTE_SUCCEEDED'),
    filter(({ payload }) => {
      const id = payload.response.id;
      const { pendingRequests } = state$.value.data.notes;

      return pendingRequests.update.includes(id) || pendingRequests.delete.includes(id);
    }),
    mergeMap(({ payload }) => {
      const id = payload.response.id;
      const { collection, currentlyEditing, pendingRequests } = state$.value.data.notes;
      // note could have already been merged into the collection
      const note = currentlyEditing.id == id ? currentlyEditing : collection.items.get(id);

      return iif(
        () => pendingRequests.delete.includes(id),
        deleteObservable(payload.response.contact, id),
        patchObservable(
          payload.response.contact,
          id,
          note,
          state$.value.ui.location.view,
          currentlyEditingNoteActions,
        ),
      );
    }),
  );
};

export const notesEpic = combineEpics(
  fetchAllNotesEpic,
  fetchNoteEpic,
  createNoteEpic,
  updateNoteEpic,
  deleteNoteEpic,
  autoSaveNoteEpic,
  autoSaveNoteTypeEpic,
  autoSaveNewNoteTypeEpic,
  autoSaveModalNoteEpic,
  pendingRequestsEpic,
);
